import cx from 'classnames'
import Avatar from 'common/Avatar/Avatar'
import { IReactNodeViewContext } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewContext'
import { mentionsConversionOptions } from 'common/Prose/utils/mentionsConversionOptions'
import { MentionSubject, SmartLinkAttributes } from 'common/Prose/utils/smartLinks/types'
import { MentionsFormatBar } from 'components/MentionsFormatBar/MentionsFormatBar'
import { UserProfileSidebar } from 'components/UserProfileSidebar/UserProfileSidebar'
import { useUserCard } from 'hooks/queries/users/useUserCard'
import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { Provider, useSelector } from 'react-redux'
import store from 'redux/store'
import { RootState } from 'types'
import { getInitials } from 'utils'
import { type EditableSmartLinkChildProps } from './EditableSmartLink'
import Styles from './UserSmartLink.module.scss'

export const UserSmartLink = (props: EditableSmartLinkChildProps) => {
  const {
    sanitizedHTML,
    nodeViewContext,
    dataProps,
    showMentionFormatBar,
    setShowMentionFormatBar,
    parentRef,
    isUserSidebarOpen,
    setIsUserSidebarOpen,
  } = props
  const { node, getPos, view } = nodeViewContext as IReactNodeViewContext
  const attrs = node.attrs
  const { userId, type, url, title } = attrs as SmartLinkAttributes
  const currentWorkspaceId = useSelector((state: RootState) => state.workspace.currentWorkspaceId)

  const [mentionSubject, setMentionSubject] = useState<MentionSubject | null>({
    title: title,
    avatar: { emoji: <Avatar size={16} label={'U'} /> },
  })

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { data: userData } = useUserCard(parseInt(userId!))

  useEffect(() => {
    if (!userData) return
    const { display_name, avatar_url, company_role } = userData.user
    setMentionSubject({
      avatar: { label: getInitials(display_name), url: avatar_url },
      title: display_name,
      role: company_role as string,
    })
  }, [type, userData])

  const displayTitle = useMemo(() => mentionSubject?.title || title, [mentionSubject?.title, title])

  const handleVisitLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    setIsUserSidebarOpen(!isUserSidebarOpen)
  }

  return (
    <>
      {showMentionFormatBar && (
        <MentionsFormatBar
          view={view}
          close={() => setShowMentionFormatBar(false)}
          getPos={getPos}
          mentionSubject={mentionSubject}
          subtitle={mentionSubject?.role ?? ''}
          smartLinkUuid={dataProps['data-uuid']}
          link={url}
          node={node}
          onLinkClick={handleVisitLinkClick}
          conversionOptions={mentionsConversionOptions(node, getPos(), view, displayTitle)}
          parentRef={parentRef}
          forceFormatBarVisible={setShowMentionFormatBar}
          linkPosition={{ from: getPos(), to: getPos() + node.nodeSize }}
        />
      )}
      <a
        data-test-id="smart-link-editable"
        {...dataProps}
        className={cx({ [Styles.showingMentionFormatBar]: showMentionFormatBar })}
        href={url}
        onClick={handleVisitLinkClick}
        tabIndex={-1}
        target="_blank"
        rel="noreferrer"
        dangerouslySetInnerHTML={sanitizedHTML}
      ></a>

      <Provider store={store}>
        <UserProfileSidebar
          open={isUserSidebarOpen}
          onClose={() => setIsUserSidebarOpen(false)}
          workspaceId={currentWorkspaceId}
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          memberId={parseInt(userId!)}
          sidebarClassName={Styles.sidebar}
        />
      </Provider>
    </>
  )
}
